// apps/admin-ui/src/components/gauges/PegStatusGauge.tsx
// Static SVG gauge for USD peg status display
// Zero JavaScript - pure CSS/SVG for instant render
// ALL RIGHTS RESERVED

import '../../styles/gauges.css';

export interface PegStatusGaugeProps {
  price: number;    // Current peg price (e.g., 1.0002)
  stable: boolean;  // Whether peg is considered stable
}

/**
 * Peg Status Gauge
 * 
 * Displays USD peg price as a semicircular gauge.
 * Uses static SVG - no JavaScript animations or timers.
 * 
 * Visual scale: $0.98 to $1.02 (±2% range)
 * Stability: Green if stable, warning otherwise
 */
export function PegStatusGauge({ price, stable }: PegStatusGaugeProps) {
  // Map price to percentage (0.98 = 0%, 1.00 = 50%, 1.02 = 100%)
  const minPrice = 0.98;
  const maxPrice = 1.02;
  const clampedPrice = Math.max(minPrice, Math.min(maxPrice, price));
  const percentage = ((clampedPrice - minPrice) / (maxPrice - minPrice)) * 100;
  
  // Arc path calculations
  const arcLength = 125.6;
  const fillLength = (arcLength * percentage) / 100;
  const dashOffset = arcLength - fillLength;
  
  // Determine color based on stability
  const gaugeColor = stable ? 'var(--color-success)' : 'var(--color-warning)';
  
  // Format price for display
  const displayPrice = `$${price.toFixed(4)}`;
  
  return (
    <div className="gauge gauge--peg-status">
      <svg viewBox="0 0 100 55" className="gauge-svg">
        {/* Background arc */}
        <path
          d="M10,50 A40,40 0 0,1 90,50"
          fill="none"
          stroke="var(--color-bg-muted)"
          strokeWidth="8"
          strokeLinecap="round"
        />
        
        {/* Filled arc */}
        <path
          d="M10,50 A40,40 0 0,1 90,50"
          fill="none"
          stroke={gaugeColor}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={arcLength}
          strokeDashoffset={dashOffset}
          className="gauge-fill"
        />
        
        {/* Center marker ($1.00 = 50%) */}
        <line
          x1="50"
          y1="12"
          x2="50"
          y2="18"
          stroke="var(--color-text-muted)"
          strokeWidth="2"
          strokeLinecap="round"
        />
        
        {/* Value text */}
        <text
          x="50"
          y="38"
          textAnchor="middle"
          className="gauge-value gauge-value--small"
          fill="var(--color-text)"
        >
          {displayPrice}
        </text>
        
        {/* Status indicator */}
        <text
          x="50"
          y="50"
          textAnchor="middle"
          className="gauge-status"
          fill={gaugeColor}
        >
          {stable ? '● Stable' : '◐ Variance'}
        </text>
      </svg>
    </div>
  );
}

export default PegStatusGauge;
